import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { MultiValueInput } from '@/components/form/MultiValueInput';
import { Spinner } from '@/components/ui/spinner';
import { useAxios } from '@/hooks/useAxios';

export interface AllowedDomainsEditFormProps {
  onSuccess: () => void;
  onClose?: () => void;
  currentEmail: string;
  editable: boolean;
  onDomainsChange?: (domains: string[]) => void;
}

export const AllowedDomainsEditForm: React.FC<AllowedDomainsEditFormProps> = ({
  onSuccess,
  currentEmail,
  editable,
  onDomainsChange,
}) => {
  const axios = useAxios({ email: currentEmail, doNotImpersonate: true });

  const [domains, setDomains] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    axios
      .get<{ domains: string[] }>('/customer/domains')
      .then(res => {
        setDomains(res.data.domains ?? []);
        setLoaded(true);
      })
      .catch(() => {
        setFetchFailed(true);
        setLoaded(true);
      });
  }, [currentEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editable || fetchFailed) return;
    setSaving(true);
    try {
      const res = await axios.put<{ domains: string[] }>('/customer/domains', {
        domains,
      });
      onDomainsChange?.(res.data.domains ?? []);
      toast.success('Allowed domains updated');
      onSuccess();
    } catch {
      toast.error('Failed to update allowed domains');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <form
      id="allowed-domains-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-6"
    >
      <p className="text-chariot-text-secondary text-sm">
        Only collaborators with these email domains can be invited to this
        organization. Note: praetorian.com is always permitted.
      </p>

      <div data-testid="allowed-domains-input">
        <MultiValueInput
          values={domains}
          onChange={newDomains => {
            const normalized = newDomains.map(d => d.trim().toLowerCase());
            const domainPattern =
              /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
            const invalid = normalized.filter(
              d => d.length < 4 || d.length > 253 || !domainPattern.test(d)
            );
            if (invalid.length > 0) {
              toast.error(`Invalid domain: ${invalid.join(', ')}`);
              return;
            }
            setDomains(normalized);
          }}
          editable={editable && !saving}
          placeholder="e.g. acme.com"
          addButtonLabel="Add"
          aria-label="Allowed email domains"
        />
        {domains.length === 0 && (
          <p className="text-chariot-text-secondary mt-2 text-sm">
            No restrictions — any email domain can be invited.
          </p>
        )}
      </div>
    </form>
  );
};
